import React, { FC, useCallback, useRef } from "react";
import { Box, Button, Divider } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormControlV2, FormInputCharacterCounter } from "components/form";
import { GRS_REJECTION_NOTES_MAX_LENGTH } from "constants/grs-pending-leads";
import { ApiRequestStatusEnum } from "enums/api-request-status.enum";
import { FormControlsTypeEnum } from "enums/form-controls-type.enum";
import { TGrsPendingLeadDetail } from "types/grs-pending-lead.type";
import { TSelectOption } from "types/select-input.type";
import {
  grsPendingLeadRejectValidationSchema,
  TGrsRejectFormValues,
} from "utils/validation-schemas/grs-pending-lead-reject.validation";
import { GrsPendingLeadDetailsPanel } from "./GrsPendingLeadDetailsPanel";
import { GrsPendingLeadLoadError } from "./GrsPendingLeadLoadError";

const GRS_REASONS_ERROR_ID = "grs-pending-lead-reasons-error";

interface IGrsPendingLeadRejectStepProps {
  detail: TGrsPendingLeadDetail;
  reasonOptions: TSelectOption<number>[];
  reasonsStatus: ApiRequestStatusEnum;
  onRetryReasons: () => Promise<void>;
  defaultValues: TGrsRejectFormValues;
  isSubmitting: boolean;
  onBack: (values: TGrsRejectFormValues) => void;
  onReject: (values: TGrsRejectFormValues) => Promise<boolean>;
}

export const GrsPendingLeadRejectStep: FC<IGrsPendingLeadRejectStepProps> = ({
  detail,
  reasonOptions,
  reasonsStatus,
  onRetryReasons,
  defaultValues,
  isSubmitting,
  onBack,
  onReject,
}) => {
  const { control, handleSubmit, getValues, watch } = useForm<
    TGrsRejectFormValues
  >({
    defaultValues,
    mode: "onSubmit",
    resolver: yupResolver(grsPendingLeadRejectValidationSchema),
  });

  const notes = watch("notes");

  const handleBack = useCallback(() => onBack(getValues()), [
    getValues,
    onBack,
  ]);

  const reasonFieldRef = useRef<HTMLDivElement>(null);
  const retryReasons = useCallback(async () => {
    await onRetryReasons();
    reasonFieldRef.current?.focus();
  }, [onRetryReasons]);

  const rejectButtonRef = useRef<HTMLButtonElement>(null);
  const submitReject = useCallback(
    async (values: TGrsRejectFormValues) => {
      const succeeded = await onReject(values);
      if (!succeeded) rejectButtonRef.current?.focus();
    },
    [onReject]
  );

  const areReasonsLoaded = reasonsStatus === ApiRequestStatusEnum.SUCCESS;
  const hasReasonsError = reasonsStatus === ApiRequestStatusEnum.ERROR;

  return (
    <Box>
      <Box mb={5} color={"leadpro.700"}>
        Please select a reason for rejecting this lead. Optionally provide
        further reasoning in the notes below.
      </Box>
      <GrsPendingLeadDetailsPanel detail={detail} />
      <Divider mb={4} />
      <form onSubmit={handleSubmit(submitReject)}>
        <Box
          ref={reasonFieldRef}
          tabIndex={-1}
          role={"group"}
          aria-label={"Rejection reason"}
          _focus={{ outline: "none" }}
          mb={4}
        >
          {hasReasonsError ? (
            <GrsPendingLeadLoadError
              id={GRS_REASONS_ERROR_ID}
              message={"We couldn't load the rejection reasons."}
              onRetry={retryReasons}
            />
          ) : (
            <FormControlV2<TGrsRejectFormValues>
              name={"rejectionReasonId"}
              control={control}
              type={FormControlsTypeEnum.SINGLE_SELECT}
              isDisabled={!areReasonsLoaded || isSubmitting}
              additionalProps={{
                placeholder: areReasonsLoaded
                  ? "Select a rejection reason..."
                  : "Loading rejection reasons...",
                options: reasonOptions,
              }}
            />
          )}
        </Box>
        <FormControlV2<TGrsRejectFormValues>
          name={"notes"}
          control={control}
          type={FormControlsTypeEnum.TEXTAREA}
          isDisabled={isSubmitting}
          additionalProps={{
            textAreaRows: 5,
            placeholder: "Rejection notes (optional)",
            maxLength: GRS_REJECTION_NOTES_MAX_LENGTH,
          }}
        />
        <FormInputCharacterCounter
          currentLength={notes.length}
          maxLength={GRS_REJECTION_NOTES_MAX_LENGTH}
        />
        <Box display={"flex"} justifyContent={"flex-end"} mt={4}>
          <Button
            mr={3}
            variant={"outline"}
            onClick={handleBack}
            isDisabled={isSubmitting}
          >
            Back
          </Button>
          <Button
            ref={rejectButtonRef}
            type={"submit"}
            colorScheme={"red"}
            isDisabled={!areReasonsLoaded || isSubmitting}
            isLoading={isSubmitting}
            aria-describedby={
              hasReasonsError ? GRS_REASONS_ERROR_ID : undefined
            }
          >
            Reject
          </Button>
        </Box>
      </form>
    </Box>
  );
};
